import { GetFieldPropertiesResult } from '@indigohive/cogfy-types/endpoints/getFieldProperties'
import { Select } from '../../Select'
import { ChatMiddlewareConditionSource } from '../ChatMiddlewareConditionForm'
import { useMemo } from 'react'
import { FieldType } from '@indigohive/cogfy-types'
import { useTranslation } from 'react-i18next'

export type ChatMiddlewareConditionOperatorSelectProps = {
  source: ChatMiddlewareConditionSource
  value: string
  fields: { id: string, type: FieldType }[]
  fieldProperties?: GetFieldPropertiesResult
  onChange: (source: string) => void
}

const OPERATOR_OPTIONS = {
  content_type: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' }
  ],
  content: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'contains', value: 'contains' },
    { label: 'not contains', value: 'not_contains' },
    { label: 'starts with', value: 'starts_with' },
    { label: 'ends with', value: 'ends_with' }
  ],
  field: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  template: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ]
}

const TEMPLATE_OPERATOR_OPTIONS: Record<'text' | 'number' | 'date' | 'boolean', { label: string, value: string }[]> = {
  text: [
    { label: 'contains', value: 'contains' },
    { label: 'ends with', value: 'ends_with' },
    { label: 'equals', value: 'equals' },
    { label: 'not contains', value: 'not_contains' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'starts with', value: 'starts_with' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  number: [
    { label: 'equals', value: 'equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'greater than or equals', value: 'greater_than_or_equals' },
    { label: 'less than', value: 'less_than' },
    { label: 'less than or equals', value: 'less_than_or_equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  date: [
    { label: 'equals', value: 'equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'greater than or equals', value: 'greater_than_or_equals' },
    { label: 'less than', value: 'less_than' },
    { label: 'less than or equals', value: 'less_than_or_equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  boolean: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' }
  ]
}

export function ChatMiddlewareConditionOperatorSelect (props: ChatMiddlewareConditionOperatorSelectProps) {
  const { source, value, fields, fieldProperties, onChange } = props

  const { t } = useTranslation()

  const operatorOptions = useMemo(() => {
    if (source.type !== 'template') {
      return OPERATOR_OPTIONS[source.type]
    }

    const fieldType = fields.find(field => field.id === source.field.id)?.type

    if (!fieldType || !fieldProperties?.data) return []

    const propertyType = fieldProperties.data[fieldType].find(property => property.name === source.field.property)?.type

    if (!propertyType) return []

    return TEMPLATE_OPERATOR_OPTIONS[propertyType]
  }, [fields, fieldProperties, source])

  return (
    <Select
      size="md"
      value={value}
      options={operatorOptions.map(option =>
        ({ label: t(`conditions:${option.label}`), value: option.value })
      )}
      onChange={event => {
        onChange(event.target.value)
      }}
    />
  )
}
